import { useEffect } from 'react';

import {
  boundPrivyUserId,
  checkGrantIdentity,
  GrantIdentity,
  type PrivyBinding,
} from './privy-binding';
import {
  walletGrantExpiresAt,
  WalletGrantExpiry,
  type WalletUnlockGrant,
  walletUnlockSession,
} from './wallet-unlock-session';

type WalletCleanup = () => Promise<void>;

const WALLET_ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

const ACTIVITY_LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };

export function useWalletOwnerLifecycle(
  gammaUserId: string | null,
  walletEnabled: boolean | undefined,
  privyReady: boolean,
  lock: WalletCleanup,
): void {
  useEffect(() => {
    if (walletUnlockSession.setOwner(gammaUserId)) {
      void lock();
    }
  }, [gammaUserId, lock]);

  useEffect(() => {
    if (walletEnabled === false) {
      void lock();
    }
  }, [walletEnabled, privyReady, lock]);

  // A reload keeps a valid shared grant; without one, any Privy session left from an earlier unlock is ended.
  useEffect(() => {
    if (
      walletEnabled &&
      privyReady &&
      gammaUserId &&
      !walletUnlockSession.resumeStoredGrant(Date.now())
    ) {
      void lock();
    }
  }, [gammaUserId, walletEnabled, privyReady, lock]);

  // An unmount (error screen, hot reload) must not lock the other tabs.
  useEffect(() => () => walletUnlockSession.lockThisTab(), []);
}

export function useWalletGrantExpiry(
  grant: WalletUnlockGrant | null,
  endPrivySession: WalletCleanup,
): void {
  useEffect(() => {
    if (!grant) {
      return;
    }

    const expireIfIdle = () => {
      if (walletUnlockSession.expireIdleGrant(Date.now()) === WalletGrantExpiry.RemovedShared) {
        void endPrivySession();
      }
    };

    const timeout = window.setTimeout(
      expireIfIdle,
      Math.max(0, walletGrantExpiresAt(grant) - Date.now()),
    );
    window.addEventListener('focus', expireIfIdle);
    document.addEventListener('visibilitychange', expireIfIdle);

    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('focus', expireIfIdle);
      document.removeEventListener('visibilitychange', expireIfIdle);
    };
  }, [grant, endPrivySession]);
}

export function useWalletIdentityLock(
  grant: WalletUnlockGrant | null,
  binding: PrivyBinding,
  lock: WalletCleanup,
): void {
  useEffect(() => {
    if (!grant) {
      return;
    }

    const identity = checkGrantIdentity(binding, grant);

    if (identity === GrantIdentity.SignedOut) {
      walletUnlockSession.lockThisTab();
    }

    if (identity === GrantIdentity.Conflicting) {
      void lock();
    }
  }, [grant, binding, lock]);
}

export function useWalletBoundRestore(binding: PrivyBinding): void {
  const privyUserId = boundPrivyUserId(binding);

  useEffect(() => {
    if (privyUserId) {
      walletUnlockSession.restoreBoundGrant(privyUserId, Date.now());
    }
  }, [privyUserId]);
}

export function useWalletActivity(): void {
  useEffect(() => {
    const recordActivity = () => {
      walletUnlockSession.touch(Date.now());
    };

    WALLET_ACTIVITY_EVENTS.forEach((type) =>
      document.addEventListener(type, recordActivity, ACTIVITY_LISTENER_OPTIONS),
    );

    return () => {
      WALLET_ACTIVITY_EVENTS.forEach((type) =>
        document.removeEventListener(type, recordActivity, ACTIVITY_LISTENER_OPTIONS),
      );
    };
  }, []);
}
